import React, { useState } from 'react';
import { Modal, Form, Input, Select, Space, message } from 'antd';
import api from '../api';

// Shared by the Clients list and the client page, so the two edit the same
// way: the client fields, and the contact popup (a contact's campaigns and
// teams come from its clients' campaigns and teams).

export const CLIENT_STATUSES  = ['Prospect', 'Client', 'Former Client', 'Inactive'];
export const CONTACT_STATUSES = ['Active', 'Inactive'];
export const STATUS_COLOR = { Prospect: 'gold', Client: 'green', 'Former Client': 'default', Inactive: 'default', Active: 'green' };

export const selectProps = { mode: 'multiple', allowClear: true, optionFilterProp: 'label', style: { width: '100%' } };

// Pick lists show active campaigns/teams, plus any inactive one already linked.
export const campaignOpts = (options, selected = []) => (options.campaigns || [])
  .filter((c) => c.IsActive || selected.includes(c.ID))
  .map((c) => ({ value: c.ID, label: c.Name }));
export const teamOpts = (options, selected = []) => (options.teams || [])
  .filter((t) => t.Status === 'active' || selected.includes(t.ID))
  .map((t) => ({ value: t.ID, label: t.Name }));

// A client's fields, inside the caller's <Form>. Help Desk Queue: where its
// Client Ticket Type tickets go (blank = the Request Type's own queue).
export function ClientFields({ options, campaignIDs = [], teamIDs = [], queueID = null }) {
  return (
    <>
      <Form.Item name="name" label="Name" rules={[{ required: true, whitespace: true, message: 'Name is required' }, { max: 200 }]}>
        <Input />
      </Form.Item>
      <Space style={{ display: 'flex' }} align="start" wrap>
        <Form.Item name="state" label="State" rules={[{ max: 100 }]}><Input /></Form.Item>
        <Form.Item name="country" label="Country" rules={[{ max: 100 }]}><Input /></Form.Item>
        <Form.Item name="status" label="Status" rules={[{ required: true }]}>
          <Select style={{ width: 150 }} options={CLIENT_STATUSES.map((v) => ({ value: v, label: v }))} />
        </Form.Item>
      </Space>
      <Form.Item name="campaign_ids" label="Campaigns">
        <Select {...selectProps} options={campaignOpts(options, campaignIDs)} />
      </Form.Item>
      <Form.Item name="team_ids" label="Teams">
        <Select {...selectProps} options={teamOpts(options, teamIDs)} />
      </Form.Item>
      <Form.Item name="helpdesk_queue_id" label="Help Desk Queue">
        <Select allowClear showSearch optionFilterProp="label" style={{ width: '100%' }}
          options={(options.queues || [])
            .filter((q) => q.IsActive || q.ID === queueID)
            .map((q) => ({ value: q.ID, label: q.Name }))} />
      </Form.Item>
    </>
  );
}

// Add / edit a contact.
//   contact:        the contact being edited (list row shape), or null to add
//   presetClientIds: clients a new contact starts on (the client page)
//   clients:        the client list rows (ID, Name, CampaignIDs, TeamIDs)
//   options:        { campaigns, teams }
export function ContactModal({ open, contact, presetClientIds = [], clients, options, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  // The form is created fresh each time the popup opens (destroyOnClose), so
  // its starting values are simply these.
  const initialValues = {
    first_name:   contact?.FirstName || '',
    last_name:    contact?.LastName || '',
    title:        contact?.Title || '',
    status:       contact?.Status || 'Active',
    email:        contact?.Email || '',
    phone:        contact?.Phone || '',
    cell:         contact?.Cell || '',
    client_ids:   contact?.ClientIDs || presetClientIds,
    campaign_ids: contact?.CampaignIDs || [],
    team_ids:     contact?.TeamIDs || [],
    user_id:      contact?.UserID ?? null,
  };

  const chosenClientIds = Form.useWatch('client_ids', form) ?? initialValues.client_ids;
  const allowedFor = (clientIDs) => {
    const chosen = (clients || []).filter((c) => (clientIDs || []).includes(c.ID));
    return {
      campaigns: new Set(chosen.flatMap((c) => c.CampaignIDs || [])),
      teams:     new Set(chosen.flatMap((c) => c.TeamIDs || [])),
    };
  };
  const allowed = allowedFor(chosenClientIds);

  // Removing a client takes away the campaigns and teams only it gave.
  const onValuesChange = (changed) => {
    if (!('client_ids' in changed)) return;
    const a = allowedFor(changed.client_ids);
    form.setFieldsValue({
      campaign_ids: (form.getFieldValue('campaign_ids') || []).filter((id) => a.campaigns.has(id)),
      team_ids:     (form.getFieldValue('team_ids') || []).filter((id) => a.teams.has(id)),
    });
  };

  const save = async () => {
    let v;
    try { v = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      if (contact?.ID) await api.put(`/api/client-contacts/${contact.ID}`, v);
      else             await api.post('/api/client-contacts', v);
      onSaved?.();
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal maskClosable={false} open={open} title={contact?.ID ? 'Edit Contact' : 'Add Contact'} width={640}
      onCancel={onClose} onOk={save} okText="Save" confirmLoading={saving} destroyOnClose>
      <Form form={form} layout="vertical" onValuesChange={onValuesChange} preserve={false} initialValues={initialValues}>
        <Space style={{ display: 'flex' }} align="start">
          <Form.Item name="first_name" label="First Name" rules={[{ required: true, whitespace: true, message: 'First Name is required' }, { max: 100 }]}>
            <Input />
          </Form.Item>
          <Form.Item name="last_name" label="Last Name" rules={[{ required: true, whitespace: true, message: 'Last Name is required' }, { max: 100 }]}>
            <Input />
          </Form.Item>
        </Space>
        <Space style={{ display: 'flex' }} align="start">
          <Form.Item name="title" label="Title" rules={[{ max: 200 }]} style={{ width: 380 }}><Input /></Form.Item>
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select style={{ width: 150 }} options={CONTACT_STATUSES.map((v) => ({ value: v, label: v }))} />
          </Form.Item>
        </Space>
        <Form.Item name="email" label="Email" rules={[{ type: 'email', message: 'Email is not a valid address' }, { max: 254 }]}>
          <Input />
        </Form.Item>
        <Space style={{ display: 'flex' }} align="start">
          <Form.Item name="phone" label="Phone" rules={[{ max: 50 }]}><Input /></Form.Item>
          <Form.Item name="cell" label="Cell" rules={[{ max: 50 }]}><Input /></Form.Item>
        </Space>
        <Form.Item name="client_ids" label="Clients">
          <Select {...selectProps} options={(clients || []).map((c) => ({ value: c.ID, label: c.Name }))} />
        </Form.Item>
        <Form.Item name="campaign_ids" label="Campaigns">
          <Select {...selectProps} options={(options.campaigns || [])
            .filter((c) => allowed.campaigns.has(c.ID)).map((c) => ({ value: c.ID, label: c.Name }))} />
        </Form.Item>
        <Form.Item name="team_ids" label="Teams">
          <Select {...selectProps} options={(options.teams || [])
            .filter((t) => allowed.teams.has(t.ID)).map((t) => ({ value: t.ID, label: t.Name }))} />
        </Form.Item>
        {/* The client's own Harbor login (Role client) that is this contact:
            their tickets get this contact, and they see their company's. */}
        <Form.Item name="user_id" label="Harbor Login">
          <Select allowClear showSearch optionFilterProp="label" placeholder="Not linked" style={{ width: '100%' }}
            options={(options.client_logins || [])
              // Active logins not on another contact; this contact's own
              // stays listed after it is deactivated.
              .filter((u) => (!u.ContactID && u.IsActive) || u.ContactID === contact?.ID)
              .map((u) => ({ value: u.ID, label: u.Email ? `${u.DisplayName} (${u.Email})` : u.DisplayName }))} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
